import { createFileRoute, Link } from "@tanstack/react-router";
import { getLocalizedMeta } from "@/lib/utils/seo";
import { PageHero, Section } from "@/components/layout/Page";
import { InteractiveSupplyMap } from "@/components/InteractiveSupplyMap";
import { ShieldCheck, Globe2, Truck, MessageCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";
import { FaqSection } from "@/components/layout/FaqSection";
import { faqHeadScript } from "@/lib/utils/faq-schema";
import { ProductCard } from "@/components/ProductCard";
import { getProductsByCategory } from "@/lib/mock/products";
import tradeHero from "@/assets/optimized/hero-farm-1920.webp";

export const Route = createFileRoute("/regional-trade-middle-east-europe")({
  head: () => ({
    meta: getLocalizedMeta("regionalTrade"),
    scripts: [faqHeadScript("regionalTrade", "/regional-trade-middle-east-europe")],
  }),
  component: RegionalTradePage,
});

function RegionalTradePage() {
  const { t } = useI18n();
  // The goods that actually move on these corridors. Reuses the seafood
  // catalogue rather than restating it, so the page cannot drift from it.
  const seafood = getProductsByCategory("seafood").slice(0, 4);

  return (
    <>
      {/* A trade-route page had no imagery at all: a flat colour block asking an
          importer to move perishable goods, showing none of them. `PageHero`
          already supports a photographic band — it is the LCP element, eager and
          sized, so it reserves its space instead of shifting the layout in.
          `hero-farm-1920.webp` is 1920x1080, which is the one hero asset wide
          enough not to upscale, and its provenance is already recorded in
          docs/project/evidence/image-credits.md. */}
      <PageHero
        image={tradeHero}
        imageWidth={1920}
        imageHeight={1080}
        eyebrow={t("regionalTrade.hero.eyebrow")}
        title={t("regionalTrade.hero.title")}
        description={t("regionalTrade.hero.description")}
      />

      <Section>
        <div className="space-y-12">
          <InteractiveSupplyMap />

          <div className="grid md:grid-cols-3 gap-6 pt-4">
            <div className="glass-card rounded-2xl p-6 border border-border/80 bg-background shadow-sm space-y-3">
              <div className="h-10 w-10 rounded-xl bg-teal/20 text-navy flex items-center justify-center font-bold">
                <Globe2 className="h-5 w-5 text-mint-ink" />
              </div>
              <h3 className="font-display text-lg font-bold text-navy">
                {t("regionalTrade.card1.title")}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t("regionalTrade.card1.desc")}
              </p>
            </div>

            <div className="glass-card rounded-2xl p-6 border border-border/80 bg-background shadow-sm space-y-3">
              <div className="h-10 w-10 rounded-xl bg-teal/20 text-navy flex items-center justify-center font-bold">
                <Truck className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-display text-lg font-bold text-navy">
                {t("regionalTrade.card2.title")}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t("regionalTrade.card2.desc")}
              </p>
            </div>

            <div className="glass-card rounded-2xl p-6 border border-border/80 bg-background shadow-sm space-y-3">
              <div className="h-10 w-10 rounded-xl bg-teal/20 text-navy flex items-center justify-center font-bold">
                <ShieldCheck className="h-5 w-5 text-mint-ink" />
              </div>
              <h3 className="font-display text-lg font-bold text-navy">
                {t("regionalTrade.card3.title")}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t("regionalTrade.card3.desc")}
              </p>
            </div>
          </div>

          <div className="text-center pt-4">
            <Button
              size="lg"
              asChild
              className="rounded-xl font-bold bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Link to="/request-quote">
                <MessageCircle className="mr-2 h-4 w-4" /> {t("regionalTrade.cta")}
              </Link>
            </Button>
          </div>
        </div>
      </Section>

      {/* What actually travels these routes. A corridor page that never shows
          the goods gives a buyer nothing to price, and this was the thinnest
          page on the site at 89 lines. Every string and image here is reused
          from the catalogue and the export page, so nothing new is claimed. */}
      <Section band>
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="mb-3 inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              <span className="inline-block h-[2px] w-8 bg-primary" />
              {t("export.products.eyebrow")}
            </div>
            <h2 className="font-display text-3xl font-bold text-navy md:text-4xl">
              {t("export.products.title")}
            </h2>
          </div>
          <Link
            to="/products/$category"
            params={{ category: "seafood" }}
            className="inline-flex items-center text-sm font-semibold text-primary hover:text-navy"
          >
            {t("export.products.viewAll")} <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {seafood.map((product) => (
            <ProductCard key={product.slug} product={product} />
          ))}
        </div>
      </Section>

      {/* Cross-links. This page had no outbound internal links at all beyond a
          single quote CTA, which is both a dead end for a reader comparing
          routes and a thin-content signal. These three all exist. */}
      <Section>
        <div className="grid gap-6 md:grid-cols-3">
          <div className="card-flat p-6">
            <h3 className="font-display text-lg font-semibold text-navy">
              {t("export.related.europe.title")}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">{t("export.related.europe.body")}</p>
            <Link
              to="/seafood-export-from-turkey-to-europe"
              className="mt-4 inline-flex items-center text-sm font-semibold text-primary hover:text-navy"
            >
              {t("export.related.europe.cta")} <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
          <div className="card-flat p-6">
            <h3 className="font-display text-lg font-semibold text-navy">
              {t("export.related.solutions.title")}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("export.related.solutions.body")}
            </p>
            <Link
              to="/services-solutions"
              className="mt-4 inline-flex items-center text-sm font-semibold text-primary hover:text-navy"
            >
              {t("export.related.solutions.cta")} <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
          <div className="card-flat p-6">
            <h3 className="font-display text-lg font-semibold text-navy">
              {t("export.related.projects.title")}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("export.related.projects.body")}
            </p>
            <Link
              to="/projects"
              className="mt-4 inline-flex items-center text-sm font-semibold text-primary hover:text-navy"
            >
              {t("export.related.projects.cta")} <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </div>
      </Section>

      <FaqSection page="regionalTrade" />
    </>
  );
}
